import React, { useEffect, useMemo, useState } from "react";
import { Segment, Dimmer, Loader, Header } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import {
    generateResolver,
    Notification,
    PermissionMatrix,
    STATUS_TYPES,
    VALIDATORS,
} from "dyl-components";
import roleActions from "actions/role";
import rolesActions from "actions/roles";
import PermissionRolesForm from "./subcomponents/PermissionRoleForm";
import { Navigate, useLocation, useParams } from "react-router";
import { useForm } from "react-hook-form";
import * as Yup from "yup";

const buildPermissionSchema = (obj) => {
    const shape = {};

    for (const key in obj) {
        if (key === "description") continue;

        if (typeof obj[key] === "object" && !obj[key].access_control) {
            shape[key] = buildPermissionSchema(obj[key]);
        } else {
            shape[key] = Yup.object({
                access_control: Yup.string().required(),
                attribute: Yup.array()
                    .default([])
                    .when("access_control", {
                        is: "restricted",
                        then: (s) =>
                            s
                                .min(1, "Select at least one attribute")
                                .required("Select at least one attribute"),
                        otherwise: (s) => s.notRequired(),
                    }),
            });
        }
    }

    return Yup.object().shape(shape);
};

const getDefaultAccessByPath = (path, action) => {
    const pathStr = path.join(".");

    if (pathStr === "required_access.general") {
        return "restricted";
    }

    if (pathStr === "required_access.teams" && action === "view") {
        return "restricted";
    }

    if (pathStr === "required_access.preferences") {
        if (["view", "add"].includes(action)) return "restricted";
        if (["edit", "delete"].includes(action)) return "off";
    }

    if (pathStr.startsWith("calendar")) {
        return "restricted";
    }

    if (pathStr === "dashboards" && action === "view") {
        return "global";
    }

    return "off";
};

const normalizePermissions = (data) => {
    const isActionNode = (obj) => {
        if (!obj || typeof obj !== "object") return false;

        return (
            "access_control" in obj ||
            "options_access_control" in obj ||
            "attribute" in obj ||
            "options_attribute" in obj
        );
    };

    const normalizeNode = (node, path = []) => {
        const result = {};

        Object.entries(node).forEach(([key, value]) => {
            if (key === "description") {
                result.description = value;
                return;
            }

            if (isActionNode(value)) {
                result[key] = {
                    access_control: getDefaultAccessByPath(path, key),
                    attribute: value.attribute?.[0]
                        ? [value.attribute?.[0]]
                        : [],
                    options_access_control: value.access_control ?? [],
                    options_attribute: value.attribute ?? [],
                };
            } else if (value && typeof value === "object") {
                result[key] = normalizeNode(value, [...path, key]);
            }
        });

        return result;
    };

    return normalizeNode(data);
};

const AddEditPermissionSettings = (props) => {
    const dispatch = useDispatch();
    const location = useLocation();

    const params = useParams();
    const type = location?.state?.incomingPermission?.type || "add";
    const [redirectToView, setRedirectToView] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const {
        isReadingRole,
        userPermissionsProfile,
        defaultPermissions,
        readingDefaultPermissions,
    } = useSelector((state) => ({
        userPermissionsProfile: state?.role?.userPermissionsProfile,
        isReadingRole: state?.role?.isReadingRole,
        defaultPermissions: state?.roles?.defaultPermissions,
        readingDefaultPermissions: state?.roles?.readingDefaultPermissions,
    }));

    const normalizedDefaultPermission = useMemo(() => {
        if (!defaultPermissions) return {};
        const { requestID, ...rest } = defaultPermissions;
        return normalizePermissions(rest);
    }, [defaultPermissions]);

    const activePermissionProfile = useMemo(() => {
        return type === "view" || type === "edit" || type === "duplicate"
            ? userPermissionsProfile?.permission
            : type === "add"
              ? normalizedDefaultPermission
              : {};
    }, [type, userPermissionsProfile?.permission, normalizedDefaultPermission]);

    const updatedName =
        type === "duplicate"
            ? `Duplicate of ${userPermissionsProfile?.name}`
            : userPermissionsProfile?.name;
    const header =
        type === "edit" ? `Edit '${userPermissionsProfile?.name}'` : null;

    const permissionSchema = useMemo(() => {
        return buildPermissionSchema(activePermissionProfile || {});
    }, [activePermissionProfile]);

    const resolver = useMemo(() => {
        return generateResolver({
            name: VALIDATORS.TEAM_NAME(true)
                .required("This field is required")
                .trim(),
            description: VALIDATORS.DESCRIPTION(true).required(
                "This field is required",
            ),
            permission: permissionSchema,
        });
    }, [permissionSchema]);

    const {
        control,
        trigger,
        reset,
        watch,
        formState: { isValid, isDirty, errors },
        getValues,
        setValue,
        setError,
    } = useForm({
        mode: "onChange",
        resolver,
        defaultValues: {
            name: type === "add" ? "" : updatedName,
            description:
                type === "add" ? "" : userPermissionsProfile?.description || "",
            permission: activePermissionProfile,
        },
    });

    const onSave = async ({ name, description, permission }, setError) => {
        setIsSaving(true);
        const newRole = {
            name: name.trim(),
            description: description.trim(),
            permission: permission || activePermissionProfile,
        };

        const isEdit = type === "edit";
        const action = isEdit
            ? rolesActions.updateRole(params?.roleId, newRole)
            : rolesActions.createRole(newRole);

        try {
            await dispatch(action);
            setRedirectToView(true);
            setIsSaving(false);
        } catch (error) {
            const isDuplicate = error?.Message?.includes?.("duplicate");
            const errorMessage = isDuplicate
                ? "Permission Profile Role Name already exists"
                : `Failed to ${isEdit ? "update" : "create"} Role`;

            if (isDuplicate && setError) {
                setError("name", {
                    type: "unique",
                    message: errorMessage,
                });
            }

            setIsSaving(false);
            Notification.alert(errorMessage, STATUS_TYPES.ERROR, true);
        }
    };

    const uniqueNameValidate = () => {
        if (
            !getValues("name") ||
            (getValues("name") === updatedName && type === "edit")
        )
            return;

        dispatch(
            rolesActions.validatePermissionName("", {
                name: getValues("name"),
            }),
        )
            .then((exists) => {
                if (exists) {
                    setError("name", {
                        type: "unique",
                        message: "Permission Profile Role Name already exists",
                    });
                }
            })
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        const roleId =
            params?.roleId || location?.state?.incomingPermission?.permid;
        if (!roleId) return;

        dispatch(roleActions.readUserRole(roleId));
    }, [dispatch, location?.state?.incomingPermission?.permid, params]);

    useEffect(() => {
        if (type === "add") {
            dispatch(rolesActions.getPermissions());
        }
    }, [dispatch, type]);

    useEffect(() => {
        if (
            activePermissionProfile &&
            Object.keys(activePermissionProfile).length > 0
        ) {
            reset({
                name: type === "add" ? "" : updatedName,
                description:
                    type === "add"
                        ? ""
                        : userPermissionsProfile?.description || "",
                permission: activePermissionProfile,
            });
        }
    }, [
        activePermissionProfile,
        reset,
        type,
        updatedName,
        userPermissionsProfile?.description,
    ]);

    if (redirectToView) return <Navigate to="/settings/permission-profile" />;

    return (
        <div>
            {isReadingRole ? (
                <Dimmer active>
                    <Loader />
                </Dimmer>
            ) : (
                <>
                    {header && <Header as="h3">Edit "{updatedName}"</Header>}
                    <PermissionRolesForm
                        onSave={onSave}
                        control={control}
                        uniqueNameValidate={uniqueNameValidate}
                        getValues={getValues}
                        isDirty={isDirty && type !== "view"}
                        isValid={Boolean(
                            isValid && Object.keys(errors || {})?.length === 0,
                        )}
                        setError={setError}
                        permissionConfigs={
                            readingDefaultPermissions ? (
                                <Segment>
                                    <Dimmer active>
                                        <Loader active />
                                    </Dimmer>
                                </Segment>
                            ) : (
                                <PermissionMatrix
                                    permissions={watch("permission")}
                                    onChange={(data) => {
                                        setValue("permission", data, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    readOnly={type === "view"}
                                    control={control}
                                    trigger={trigger}
                                />
                            )
                        }
                        name={updatedName}
                        header={header}
                        type={type}
                        isSaving={isSaving}
                    />
                </>
            )}
        </div>
    );
};

export default AddEditPermissionSettings;
